import { useState, useEffect, useCallback } from 'react';
import {
  X, Check, AlertCircle, Loader2, ExternalLink,
  Github, FileText, MessageSquare, Mail, Search,
  Shield, Lock, ChevronRight, ArrowLeft, RefreshCw,
  GitPullRequest, CircleDot, Filter, Building2,
  Sparkles, Database, Eye, EyeOff, HelpCircle, Key, Layers
} from 'lucide-react';
import api from '@/api';
import { LinearLogo, HarnessLogo, CriblLogo, BuildkiteLogo, DatadogLogo, PagerDutyLogo } from './logos';

// GitHub Logo SVG
function GitHubLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

// Slack Logo SVG
function SlackLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 127 127" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A" />
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0" />
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D" />
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" fill="#ECB22E" />
    </svg>
  );
}

// Notion Logo SVG
function NotionLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.017 4.313l55.333-4.087c6.797-.583 8.543-.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277-1.553 6.807-6.99 7.193L24.467 99.967c-4.08.193-6.023-.39-8.16-3.113L3.3 79.94c-2.333-3.113-3.3-5.443-3.3-8.167V11.113c0-3.497 1.553-6.413 6.017-6.8z" fill="#fff" />
      <path fillRule="evenodd" clipRule="evenodd" d="M61.35.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257-3.89c5.433-.387 6.99-2.917 6.99-7.193V20.64c0-2.21-.873-2.847-3.443-4.733L75.91 3.523l-.027-.02c-4.167-3.063-5.963-3.473-12.543-2.893L61.35.227zM25.663 24.707c-5.32.325-6.53.395-9.543-1.893l-6.85-5.53c-.78-.727-.39-1.553 1.36-1.747l53.76-3.887c4.467-.387 6.8.973 8.537 2.333l7.96 5.727c.39.193.973 1.167.193 1.167l-55.123 3.437-.297.393zm-6.25 65.553V34.837c0-2.333.778-3.497 2.917-3.69l59.733-3.497c2.14-.193 2.917 1.167 2.917 3.5v55.04c0 2.33-.39 4.273-3.693 4.467l-57.017 3.303c-3.307.193-4.857-.97-4.857-3.7zm56.24-53.677c.39 1.553 0 3.107-1.557 3.3l-2.717.583v40.83c-2.333 1.167-4.47 1.943-6.22 1.943-2.917 0-3.693-.967-5.833-3.497L41.763 49.1v28.96l5.833 1.36s0 3.107-4.077 3.107l-11.267.583c-.393-.777 0-2.723 1.357-3.11l2.917-.78V40.96l-4.08-.387c-.39-1.553.584-3.883 3.11-4.077l12.057-.78 20.027 30.693V40.38l-4.857-.583c-.39-1.94.973-3.303 2.723-3.497l11.267-.777z" fill="#000" />
    </svg>
  );
}

// Jira Logo SVG
function JiraLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="jira-gradient-1" x1="99.68%" y1="15.71%" x2="24.44%" y2="64.27%">
          <stop offset="18%" stopColor="#0052CC" />
          <stop offset="100%" stopColor="#2684FF" />
        </linearGradient>
        <linearGradient id="jira-gradient-2" x1="0.59%" y1="84.87%" x2="75.74%" y2="35.73%">
          <stop offset="18%" stopColor="#0052CC" />
          <stop offset="100%" stopColor="#2684FF" />
        </linearGradient>
      </defs>
      <path d="M11.53 2c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35V2.73A.73.73 0 0019.87 2H11.53z" fill="url(#jira-gradient-1)" />
      <path d="M7.33 6.35c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35V7.08a.73.73 0 00-.73-.73H7.33z" fill="url(#jira-gradient-2)" />
      <path d="M3.13 10.7c0 2.4 1.97 4.35 4.4 4.35h.26v.25c0 2.4 1.97 4.35 4.4 4.35v-8.22a.73.73 0 00-.73-.73H3.13z" fill="#2684FF" />
    </svg>
  );
}

// GitLab Logo SVG
function GitLabLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024" fill="#FC6D26" />
      <path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#E24329" />
      <path d="M12 23.054l-4.418-13.6H1.387L12 23.053z" fill="#FC6D26" />
      <path d="M1.387 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.387 9.452z" fill="#FCA326" />
      <path d="M1.387 9.452h6.195L4.918 1.263a.455.455 0 00-.867 0L1.387 9.452z" fill="#E24329" />
      <path d="M12 23.054l4.418-13.6h6.195L12 23.053z" fill="#FC6D26" />
      <path d="M22.613 9.452l1.342 4.135a.924.924 0 01-.331 1.023L12 23.054l10.613-13.602z" fill="#FCA326" />
      <path d="M22.613 9.452h-6.195l2.664-8.189a.455.455 0 01.867 0l2.664 8.189z" fill="#E24329" />
    </svg>
  );
}

// Integration source configuration - using design system tokens
const SOURCES = [
  {
    id: 'github',
    name: 'GitHub',
    description: 'Repos, PRs, Issues',
    icon: GitHubLogo,
    color: 'gray',
    bgColor: 'bg-foreground',
    textColor: 'text-background',
    available: true,
    comingSoon: false,
    oneClick: true,  // Magic one-click connection
    tagline: 'One click to connect'
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Channels & threads',
    icon: SlackLogo,
    color: 'purple',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    available: true,
    comingSoon: false,
    oneClick: true,
    tagline: 'One click to connect'
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Issues, Epics & Sprints',
    icon: JiraLogo,
    color: 'blue',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
    available: true,
    comingSoon: false,
    oneClick: true,
    tagline: 'One click to connect'
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    description: 'Projects, MRs & Pipelines',
    icon: GitLabLogo,
    color: 'orange',
    bgColor: 'bg-warning/10',
    textColor: 'text-warning',
    available: true,
    comingSoon: false,
    oneClick: true,
    tagline: 'One click to connect'
  },
  {
    id: 'linear',
    name: 'Linear',
    description: 'Issues, Cycles & Projects',
    icon: LinearLogo,
    color: 'violet',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    available: true,
    comingSoon: false,
    oneClick: true,
    tagline: 'One click to connect'
  },
  {
    id: 'harness',
    name: 'Harness',
    description: 'Pipelines & Deployments',
    icon: HarnessLogo,
    color: 'cyan',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
    available: true,
    comingSoon: false,
    apiKey: true,  // Uses API key auth instead of OAuth
    tagline: 'Connect with API key'
  },
  {
    id: 'cribl',
    name: 'Cribl',
    description: 'Pipelines & data routing',
    icon: CriblLogo,
    color: 'teal',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
    available: true,
    comingSoon: false,
    oneClick: false,  // Uses API credentials form (client credentials flow)
    tagline: 'Connect with API credentials'
  },
  {
    id: 'buildkite',
    name: 'Buildkite',
    description: 'Pipelines, builds & agents',
    icon: BuildkiteLogo,
    color: 'emerald',
    bgColor: 'bg-success/10',
    textColor: 'text-success',
    available: true,
    comingSoon: false,
    oneClick: false,  // Uses API token form
    tagline: 'Connect with API token'
  },
  {
    id: 'datadog',
    name: 'Datadog',
    description: 'Monitors, dashboards & events',
    icon: DatadogLogo,
    color: 'purple',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    available: true,
    comingSoon: false,
    oneClick: false,  // Uses API key + App key form
    tagline: 'Connect with API keys'
  },
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    description: 'On-call schedules & incidents',
    icon: PagerDutyLogo,
    color: 'green',
    bgColor: 'bg-success/10',
    textColor: 'text-success',
    available: true,
    comingSoon: false,
    oneClick: false,  // Uses API token + routing key form
    tagline: 'Connect with API token'
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Docs & wikis',
    icon: NotionLogo,
    color: 'gray',
    bgColor: 'bg-muted',
    textColor: 'text-muted-foreground',
    available: false,
    comingSoon: true
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Email conversations',
    icon: Mail,
    color: 'red',
    bgColor: 'bg-destructive/10',
    textColor: 'text-destructive',
    available: false,
    comingSoon: true
  }
];

// Modal step types
const STEPS = {
  SOURCE_PICKER: 'source_picker',
  CREDENTIALS_SETUP: 'credentials_setup',
  OAUTH_PENDING: 'oauth_pending',
  SELECTION: 'selection',  // Works for both repos (GitHub) and channels (Slack)
  INDEXING: 'indexing',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Copy button component
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="text-blue-600 hover:text-blue-700 text-xs font-medium">
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export default function VedayaConnect({ isOpen, onClose, onSuccess, initialProvider, mode = 'connect' }) {
  // mode: 'connect' = connect new integration, 'manage' = manage existing connection
  const [step, setStep] = useState(STEPS.SOURCE_PICKER);
  const [selectedSource, setSelectedSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // OAuth state
  const [sessionId, setSessionId] = useState(null);
  const [connectionId, setConnectionId] = useState(null);

  // Credentials state (for GitHub)
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);

  // Slack credentials state
  const [slackClientId, setSlackClientId] = useState('');
  const [slackClientSecret, setSlackClientSecret] = useState('');
  const [slackSigningSecret, setSlackSigningSecret] = useState('');
  const [slackRedirectUrl, setSlackRedirectUrl] = useState('');

  // Jira credentials state
  const [jiraClientId, setJiraClientId] = useState('');
  const [jiraClientSecret, setJiraClientSecret] = useState('');

  // GitLab credentials state
  const [gitlabClientId, setGitlabClientId] = useState('');
  const [gitlabClientSecret, setGitlabClientSecret] = useState('');
  const [gitlabUrl, setGitlabUrl] = useState('https://gitlab.com');

  // Linear credentials state
  const [linearClientId, setLinearClientId] = useState('');
  const [linearClientSecret, setLinearClientSecret] = useState('');
  const [criblClientId, setCriblClientId] = useState('');
  const [criblClientSecret, setCriblClientSecret] = useState('');
  const [criblUrl, setCriblUrl] = useState('https://cloud.cribl.io');

  // Harness credentials state (API key auth)
  const [harnessApiKey, setHarnessApiKey] = useState('');
  const [harnessAccountId, setHarnessAccountId] = useState('');
  const [harnessBaseUrl, setHarnessBaseUrl] = useState('https://app.harness.io');
  const [harnessOrgId, setHarnessOrgId] = useState('');
  const [harnessProjectId, setHarnessProjectId] = useState('');

  // Buildkite credentials state (API token flow)
  const [buildkiteApiToken, setBuildkiteApiToken] = useState('');
  const [buildkiteOrgSlug, setBuildkiteOrgSlug] = useState('');
  const [buildkiteOrganizations, setBuildkiteOrganizations] = useState([]);

  // Datadog credentials state (API key + App key)
  const [datadogApiKey, setDatadogApiKey] = useState('');
  const [datadogAppKey, setDatadogAppKey] = useState('');
  const [datadogSite, setDatadogSite] = useState('datadoghq.com');
  const [datadogMonitors, setDatadogMonitors] = useState([]);

  // PagerDuty credentials state (API token + routing key)
  const [pagerdutyApiToken, setPagerdutyApiToken] = useState('');
  const [pagerdutyRoutingKey, setPagerdutyRoutingKey] = useState('');
  const [pagerdutyServices, setPagerdutyServices] = useState([]);

  // Pipelines state (for Harness and Buildkite)
  const [pipelines, setPipelines] = useState([]);

  // Selection state (works for GitHub repos, Slack channels, Jira projects, GitLab projects, Linear teams, Harness pipelines, Cribl worker groups, and Buildkite pipelines)
  const [provider, setProvider] = useState(null);  // 'github', 'slack', 'jira', 'gitlab', 'linear', 'harness', 'cribl', or 'buildkite'
  const [repos, setRepos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [projects, setProjects] = useState([]);  // Jira projects
  const [teams, setTeams] = useState([]);  // Linear teams
  const [workerGroups, setWorkerGroups] = useState([]);  // Cribl worker groups
  const [selectedItems, setSelectedItems] = useState([]);  // repo_full_names, channel_ids, project_keys, or team_ids
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [orgs, setOrgs] = useState([]);
  const [connectionInfo, setConnectionInfo] = useState(null);

  // Public repo input state
  const [publicRepoUrl, setPublicRepoUrl] = useState('');
  const [addingPublicRepo, setAddingPublicRepo] = useState(false);

  // Indexing state
  const [indexingStatus, setIndexingStatus] = useState({
    items: 0,
    synced: 0
  });

  // Connected sources state
  const [connectedSources, setConnectedSources] = useState({});

  // Fetch connection status when modal opens
  const fetchConnectionStatus = async () => {
    try {
      const [githubRes, slackRes, jiraRes, gitlabRes, linearRes, harnessRes, criblRes, buildkiteRes, datadogRes, pagerdutyRes] = await Promise.all([
        api.get('/github/status').catch(() => ({ data: { connected: false } })),
        api.get('/slack/status').catch(() => ({ data: { status: 'not_configured' } })),
        api.get('/jira/status').catch(() => ({ data: { connected: false } })),
        api.get('/gitlab/status').catch(() => ({ data: { connected: false } })),
        api.get('/linear/status').catch(() => ({ data: { connected: false } })),
        api.get('/harness/status').catch(() => ({ data: { connected: false } })),
        api.get('/cribl/status').catch(() => ({ data: { connected: false } })),
        api.get('/buildkite/status').catch(() => ({ data: { connected: false } })),
        api.get('/datadog/status').catch(() => ({ data: { connected: false } })),
        api.get('/pagerduty/status').catch(() => ({ data: { connected: false } }))
      ]);

      setConnectedSources({
        github: githubRes.data?.connected,
        slack: slackRes.data?.status === 'connected' || slackRes.data?.workspace?.team_id,
        jira: jiraRes.data?.connected,
        gitlab: gitlabRes.data?.connected,
        linear: linearRes.data?.connected,
        harness: harnessRes.data?.connected,
        cribl: criblRes.data?.connected,
        buildkite: buildkiteRes.data?.connected,
        datadog: datadogRes.data?.connected,
        pagerduty: pagerdutyRes.data?.connected
      });
    } catch (err) {
      console.error('Failed to fetch connection status:', err);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(STEPS.SOURCE_PICKER);
      setSelectedSource(null);
      setLoading(false);
      setError(null);
      setSessionId(null);
      setConnectionId(null);
      setProvider(null);
      setRepos([]);
      setChannels([]);
      setProjects([]);
      setTeams([]);
      setSelectedItems([]);
      setSearchQuery('');
      setSelectedOrg('all');
      setOrgs([]);
      setConnectionInfo(null);
      setClientId('');
      setClientSecret('');
      setSlackClientId('');
      setSlackClientSecret('');
      setSlackSigningSecret('');
      setSlackRedirectUrl('');
      setJiraClientId('');
      setJiraClientSecret('');
      setGitlabClientId('');
      setGitlabClientSecret('');
      setGitlabUrl('https://gitlab.com');
      setLinearClientId('');
      setLinearClientSecret('');
      setHarnessApiKey('');
      setHarnessAccountId('');
      setHarnessBaseUrl('https://app.harness.io');
      setHarnessOrgId('');
      setHarnessProjectId('');
      setCriblClientId('');
      setCriblClientSecret('');
      setCriblUrl('https://cloud.cribl.io');
      setBuildkiteApiToken('');
      setBuildkiteOrgSlug('');
      setBuildkiteOrganizations([]);
      setDatadogApiKey('');
      setDatadogAppKey('');
      setDatadogSite('datadoghq.com');
      setDatadogMonitors([]);
      setPagerdutyApiToken('');
      setPagerdutyRoutingKey('');
      setPagerdutyServices([]);
      setPipelines([]);
      setPublicRepoUrl('');
      setAddingPublicRepo(false);
      // Fetch connection status
      fetchConnectionStatus();
    }
  }, [isOpen]);

  // Handle 'manage' mode - go directly to selection for existing connections
  useEffect(() => {
    if (isOpen && mode === 'manage' && initialProvider) {
      const source = SOURCES.find(s => s.id === initialProvider);
      if (source && source.available) {
        const timer = setTimeout(async () => {
          setSelectedSource(source);
          setProvider(initialProvider);
          setLoading(true);
          try {
            // Fetch existing connection status
            const statusRes = await api.get(`/${initialProvider}/status`);
            if (statusRes.data.connected && statusRes.data.connection_id) {
              setConnectionId(statusRes.data.connection_id);
              setConnectionInfo({
                login: statusRes.data.account?.login,
                avatar_url: statusRes.data.account?.avatar_url,
                team_name: statusRes.data.workspace?.team_name
              });
              // Load options directly
              const optionsRes = await api.get(`/connect/${statusRes.data.connection_id}/options`);
              if (optionsRes.data.provider === 'github') {
                setRepos(optionsRes.data.repos || []);
                setOrgs(optionsRes.data.orgs || []);
                setSelectedItems(optionsRes.data.selected_repos || statusRes.data.repos_selected || []);
              } else if (optionsRes.data.provider === 'slack') {
                // Handle Slack channels
                setChannels(optionsRes.data.channels || []);
                setSelectedItems(optionsRes.data.selected_channels || statusRes.data.monitored_channels || []);
              }
              setStep(STEPS.SELECTION);
            }
          } catch (err) {
            console.error('Failed to load existing connection:', err);
            setError('Failed to load connection');
          } finally {
            setLoading(false);
          }
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, mode, initialProvider]);

  // Auto-start OAuth when initialProvider is specified (e.g., from "Select Repositories")
  useEffect(() => {
    if (isOpen && initialProvider && mode !== 'manage') {
      const source = SOURCES.find(s => s.id === initialProvider);
      if (source && source.available) {
        // Small delay to ensure reset effect runs first
        const timer = setTimeout(() => {
          setSelectedSource(source);
          // Directly start OAuth for the specified provider
          const startProviderOAuth = async () => {
            setLoading(true);
            setError(null);
            try {
              const response = await api.post('/connect/session', {
                provider: initialProvider,
                return_to: window.location.pathname
              });
              const { session_id, connect_url } = response.data;
              setSessionId(session_id);
              setProvider(initialProvider);
              // Open OAuth popup
              const width = 600;
              const height = 700;
              const left = window.screenX + (window.outerWidth - width) / 2;
              const top = window.screenY + (window.outerHeight - height) / 2;
              const popup = window.open(connect_url, 'vedaya_connect_oauth', `width=${width},height=${height},left=${left},top=${top},popup=yes`);
              if (popup) {
                setStep(STEPS.OAUTH_PENDING);
              } else {
                // Popup blocked - redirect instead
                window.location.href = connect_url;
              }
            } catch (err) {
              console.error('OAuth start failed:', err);
              if (err.response?.status === 400) {
                setStep(STEPS.CREDENTIALS_SETUP);
              } else {
                setError(err.response?.data?.detail || 'Failed to start connection');
                setStep(STEPS.ERROR);
              }
              setLoading(false);
            }
          };
          startProviderOAuth();
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, initialProvider, mode]);

  // Check URL params for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const githubConnected = params.get('github_connected');
    const slackConnected = params.get('slack_connected');
    const jiraConnected = params.get('jira_connected');
    const linearConnected = params.get('linear_connected');
    const connId = params.get('connection_id');
    const login = params.get('login');
    const workspace = params.get('workspace');
    const siteUrl = params.get('site_url');
    const displayName = params.get('display_name');
    const orgName = params.get('organization_name');
    const userName = params.get('user_name');
    const oauthError = params.get('error');
    const errorMessage = params.get('message');

    if (githubConnected === 'true' && connId) {
      // GitHub OAuth success - load repo options
      setConnectionId(connId);
      setConnectionInfo({ login });
      setSelectedSource(SOURCES.find(s => s.id === 'github'));
      setProvider('github');
      setStep(STEPS.SELECTION);
      loadOptions(connId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (slackConnected === 'true' && connId) {
      // Slack OAuth success - load channel options
      setConnectionId(connId);
      setConnectionInfo({ team_name: workspace });
      setSelectedSource(SOURCES.find(s => s.id === 'slack'));
      setProvider('slack');
      setStep(STEPS.SELECTION);
      loadOptions(connId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (jiraConnected === 'true' && connId) {
      // Jira OAuth success - load project options
      setConnectionId(connId);
      setConnectionInfo({ site_url: siteUrl, display_name: displayName });
      setSelectedSource(SOURCES.find(s => s.id === 'jira'));
      setProvider('jira');
      setStep(STEPS.SELECTION);
      loadOptions(connId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (linearConnected === 'true' && connId) {
      // Linear OAuth success - load team options
      setConnectionId(connId);
      setConnectionInfo({ organization_name: orgName, user_name: userName });
      setSelectedSource(SOURCES.find(s => s.id === 'linear'));
      setProvider('linear');
      setStep(STEPS.SELECTION);
      loadOptions(connId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (oauthError) {
      setError(errorMessage || 'OAuth failed');
      setStep(STEPS.ERROR);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load options (repos or channels) after OAuth
  const loadOptions = async (connId) => {
    setLoading(true);
    try {
      const response = await api.get(`/connect/${connId}/options`);
      const data = response.data;

      if (data.provider === 'github') {
        setProvider('github');
        setRepos(data.repos || []);
        setOrgs(data.orgs || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          login: data.login,
          avatar_url: data.avatar_url
        });
      } else if (data.provider === 'slack') {
        setProvider('slack');
        setChannels(data.channels || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          team_name: data.team_name,
          team_id: data.team_id
        });
      } else if (data.provider === 'jira') {
        setProvider('jira');
        setProjects(data.projects || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          site_url: data.site_url,
          display_name: data.display_name,
          avatar_url: data.avatar_url
        });
      } else if (data.provider === 'gitlab') {
        setProvider('gitlab');
        setProjects(data.projects || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          username: data.username,
          name: data.name,
          avatar_url: data.avatar_url,
          gitlab_url: data.gitlab_url
        });
      } else if (data.provider === 'linear') {
        setProvider('linear');
        setTeams(data.teams || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          organization_name: data.organization_name,
          user_name: data.user_name,
          avatar_url: data.avatar_url
        });
      } else if (data.provider === 'harness') {
        setProvider('harness');
        setPipelines(data.pipelines || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          account_identifier: data.account_identifier,
          account_name: data.account_name,
          base_url: data.base_url
        });
      } else if (data.provider === 'cribl') {
        setProvider('cribl');
        setWorkerGroups(data.worker_groups || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          organization_name: data.organization_name,
          user_name: data.user_name,
          cribl_url: data.cribl_url
        });
      } else if (data.provider === 'buildkite') {
        setProvider('buildkite');
        setPipelines(data.pipelines || []);
        setSelectedItems(data.default_selection || []);
        setConnectionInfo({
          organization_name: data.organization_name,
          organization_slug: data.organization_slug,
          user_name: data.user_name,
          avatar_url: data.avatar_url
        });
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to load options:', err);
      setError('Failed to load options');
      setStep(STEPS.ERROR);
      setLoading(false);
    }
  };

  // Check if credentials are configured for a source
  const checkCredentials = async (sourceId) => {
    try {
      if (sourceId === 'github') {
        const response = await api.get('/github/credentials/status');
        return response.data.configured;
      } else if (sourceId === 'slack') {
        const response = await api.get('/slack/status');
        return response.data.oauth_available;
      } else if (sourceId === 'jira') {
        const response = await api.get('/jira/credentials/status');
        return response.data.configured;
      } else if (sourceId === 'linear') {
        const response = await api.get('/linear/credentials/status');
        return response.data.configured;
      } else if (sourceId === 'cribl') {
        const response = await api.get('/cribl/credentials/status');
        return response.data.configured;
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  // Start OAuth flow for a source
  const handleSourceClick = async (source) => {
    if (!source.available) return;

    setSelectedSource(source);
    setLoading(true);
    setError(null);

    // Harness uses API key auth - go directly to credentials setup
    if (source.id === 'harness') {
      setStep(STEPS.CREDENTIALS_SETUP);
      setLoading(false);
      return;
    }

    // GitHub, Slack, Jira, GitLab, and Linear are one-click magic - credentials pre-configured via env vars
    // Note: Cribl uses client credentials flow (API key), not OAuth popup
    if (source.id === 'github' || source.id === 'slack' || source.id === 'jira' || source.id === 'gitlab' || source.id === 'linear') {
      try {
        await startOAuth(source.id);
      } catch (err) {
        // If OAuth fails, might need admin to configure - show setup
        console.error(`${source.name} OAuth failed:`, err);
        if (err.response?.status === 400) {
          setStep(STEPS.CREDENTIALS_SETUP);
          setLoading(false);
        } else {
          setError(err.response?.data?.detail || `Failed to connect to ${source.name}`);
          setStep(STEPS.ERROR);
          setLoading(false);
        }
      }
      return;
    }

    // For other sources, check if credentials are configured first
    try {
      const credentialsConfigured = await checkCredentials(source.id);

      if (!credentialsConfigured) {
        setStep(STEPS.CREDENTIALS_SETUP);
        setLoading(false);
        return;
      }

      await startOAuth(source.id);
    } catch (err) {
      console.error('Failed to check credentials:', err);
      setStep(STEPS.CREDENTIALS_SETUP);
      setLoading(false);
    }
  };

  // Handle OAuth message from popup
  const handleOAuthMessage = useCallback((event) => {
    // Verify message is from OAuth popup
    if (!event.data || event.data.type !== 'oauth_complete') return;

    const { success, provider: msgProvider, connection_id, login, team_name, site_url, display_name, username, organization_name, user_name, cribl_url, error } = event.data;

    if (success) {
      // OAuth succeeded - load options
      setConnectionId(connection_id);
      setProvider(msgProvider);
      if (msgProvider === 'github') {
        setConnectionInfo({ login });
      } else if (msgProvider === 'slack') {
        setConnectionInfo({ team_name });
      } else if (msgProvider === 'jira') {
        setConnectionInfo({ site_url, display_name });
      } else if (msgProvider === 'gitlab') {
        setConnectionInfo({ username: username || login });
      } else if (msgProvider === 'linear') {
        setConnectionInfo({ organization_name, user_name });
      } else if (msgProvider === 'cribl') {
        setConnectionInfo({ organization_name, cribl_url });
      }
      setStep(STEPS.SELECTION);
      loadOptions(connection_id);
    } else {
      // OAuth failed
      setError(error || 'OAuth failed');
      setStep(STEPS.ERROR);
    }
    setLoading(false);
  }, []);

  // Listen for OAuth popup messages
  useEffect(() => {
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [handleOAuthMessage]);

  // Start OAuth flow (unified for both GitHub and Slack)
  const startOAuth = async (sourceId) => {
    setLoading(true);
    try {
      // Create connect session (works for both GitHub and Slack)
      const response = await api.post('/connect/session', {
        provider: sourceId,
        return_to: window.location.pathname
      });

      const { session_id, connect_url } = response.data;
      setSessionId(session_id);
      setProvider(sourceId);

      // Open OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        connect_url,
        'vedaya_connect_oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (popup) {
        setStep(STEPS.OAUTH_PENDING);

        // Poll for popup close (fallback if message doesn't arrive)
        const pollInterval = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(pollInterval);
              // Only set loading false if we're still in OAuth pending
              // (message handler might have already moved us forward)
              if (step === STEPS.OAUTH_PENDING) {
                setLoading(false);
              }
            }
          } catch (e) {
            // Cross-origin - popup still open
          }
        }, 500);
      } else {
        // Popup blocked - redirect instead
        window.location.href = connect_url;
      }
    } catch (err) {
      console.error('Failed to start OAuth:', err);
      setError(err.response?.data?.detail || 'Failed to start connection');
      setStep(STEPS.ERROR);
      setLoading(false);
    }
  };

  // Save GitHub credentials
  const handleSaveGitHubCredentials = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/github/credentials', {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim()
      });

      // Now start OAuth
      await startOAuth('github');
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setLoading(false);
    }
  };

  // Save Slack credentials
  const handleSaveSlackCredentials = async () => {
    if (!slackClientId.trim() || !slackClientSecret.trim() || !slackSigningSecret.trim() || !slackRedirectUrl.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!slackRedirectUrl.includes('/api/connect/callback/slack')) {
      setError('Redirect URL must end with /api/connect/callback/slack');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/slack/credentials', {
        client_id: slackClientId.trim(),
        client_secret: slackClientSecret.trim(),
        signing_secret: slackSigningSecret.trim(),
        redirect_uri: slackRedirectUrl.trim()
      });

      // Now start OAuth
      await startOAuth('slack');
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setLoading(false);
    }
  };

  // Save Jira credentials
  const handleSaveJiraCredentials = async () => {
    if (!jiraClientId.trim() || !jiraClientSecret.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/jira/credentials', {
        client_id: jiraClientId.trim(),
        client_secret: jiraClientSecret.trim()
      });

      // Now start OAuth
      await startOAuth('jira');
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setLoading(false);
    }
  };

  // Save GitLab credentials
  const handleSaveGitLabCredentials = async () => {
    if (!gitlabClientId.trim() || !gitlabClientSecret.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/gitlab/credentials', {
        client_id: gitlabClientId.trim(),
        client_secret: gitlabClientSecret.trim(),
        gitlab_url: gitlabUrl.trim()
      });

      // Now start OAuth
      await startOAuth('gitlab');
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setLoading(false);
    }
  };

  // Save Linear credentials
  const handleSaveLinearCredentials = async () => {
    if (!linearClientId.trim() || !linearClientSecret.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/linear/credentials', {
        client_id: linearClientId.trim(),
        client_secret: linearClientSecret.trim()
      });

      // Now start OAuth
      await startOAuth('linear');
    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials');
      setLoading(false);
    }
  };

  // Connect Harness with API key (no OAuth)
  const handleConnectHarness = async () => {
    if (!harnessApiKey.trim() || !harnessAccountId.trim() || !harnessOrgId.trim() || !harnessProjectId.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the direct Harness connect endpoint with API key
      const response = await api.post('/connect/harness', {
        api_token: harnessApiKey.trim(),
        account_identifier: harnessAccountId.trim(),
        base_url: harnessBaseUrl.trim(),
        org_identifier: harnessOrgId.trim(),
        project_identifier: harnessProjectId.trim()
      });

      const { connection_id } = response.data;
      setConnectionId(connection_id);
      setProvider('harness');
      setConnectionInfo({
        account_identifier: harnessAccountId.trim(),
        base_url: harnessBaseUrl.trim()
      });

      // Load pipeline options
      setStep(STEPS.SELECTION);
      await loadOptions(connection_id);
    } catch (err) {
      console.error('Failed to connect Harness:', err);
      // Handle Pydantic validation errors which return detail as an array
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to connect to Harness');
      setError(errorMsg);
      setLoading(false);
    }
  };

  // Connect to Cribl using client credentials flow (API key)
  const handleSaveCriblCredentials = async () => {
    if (!criblClientId.trim() || !criblClientSecret.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Direct connect using client credentials flow - no OAuth popup needed
      const response = await api.post('/cribl/connect', {
        client_id: criblClientId.trim(),
        client_secret: criblClientSecret.trim(),
        cribl_url: criblUrl.trim() || null
      });

      const { connection_id, organization_name, cribl_url: connectedUrl } = response.data;

      // Set connection info and proceed to worker group selection
      setConnectionId(connection_id);
      setProvider('cribl');
      setConnectionInfo({
        organization_name,
        cribl_url: connectedUrl
      });

      // Load worker groups for selection
      setStep(STEPS.SELECTION);
      loadOptions(connection_id);
    } catch (err) {
      console.error('Failed to connect to Cribl:', err);
      setError(err.response?.data?.detail || 'Failed to connect to Cribl. Check your credentials.');
      setLoading(false);
    }
  };

  // Connect to Buildkite using API token
  const handleSaveBuildkiteCredentials = async () => {
    if (!buildkiteApiToken.trim()) {
      setError('Please enter your API token');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First validate the token and get organizations if not selected
      if (!buildkiteOrgSlug.trim()) {
        const validateRes = await api.post('/buildkite/validate-token', {
          api_token: buildkiteApiToken.trim(),
          organization_slug: ''
        });

        if (validateRes.data.valid && validateRes.data.organizations?.length > 0) {
          setBuildkiteOrganizations(validateRes.data.organizations);
          // If only one org, auto-select it
          if (validateRes.data.organizations.length === 1) {
            setBuildkiteOrgSlug(validateRes.data.organizations[0].slug);
          }
          setLoading(false);
          return;
        } else {
          setError('Invalid token or no organizations accessible');
          setLoading(false);
          return;
        }
      }

      // Connect with token and org slug
      const response = await api.post('/buildkite/connect', {
        api_token: buildkiteApiToken.trim(),
        organization_slug: buildkiteOrgSlug.trim()
      });

      const { connection_id, organization } = response.data;

      // Set connection info and proceed to pipeline selection
      setConnectionId(connection_id);
      setProvider('buildkite');
      setConnectionInfo({
        organization_name: organization.name,
        organization_slug: organization.slug
      });

      // Load pipelines for selection
      setStep(STEPS.SELECTION);
      loadOptions(connection_id);
    } catch (err) {
      console.error('Failed to connect to Buildkite:', err);
      setError(err.response?.data?.detail || 'Failed to connect to Buildkite. Check your API token.');
      setLoading(false);
    }
  };

  // Connect to Datadog using API key and Application key
  const handleSaveDatadogCredentials = async () => {
    if (!datadogApiKey.trim()) {
      setError('Please enter your Datadog API Key');
      return;
    }
    if (!datadogAppKey.trim()) {
      setError('Please enter your Datadog Application Key');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First validate the keys
      const validateRes = await api.post('/datadog/validate-keys', {
        api_key: datadogApiKey.trim(),
        app_key: datadogAppKey.trim(),
        site: datadogSite
      });

      if (!validateRes.data.valid) {
        setError(validateRes.data.error || 'Invalid API keys');
        setLoading(false);
        return;
      }

      // Connect with API keys
      const response = await api.post('/datadog/connect', {
        api_key: datadogApiKey.trim(),
        app_key: datadogAppKey.trim(),
        site: datadogSite
      });

      const { connection_id, organization, monitor_count, dashboard_count } = response.data;

      // Set connection info
      setConnectionId(connection_id);
      setProvider('datadog');
      setConnectionInfo({
        organization_name: organization.name,
        monitor_count,
        dashboard_count,
        site: datadogSite
      });

      // Datadog connected successfully - go to success page (no selection needed for now)
      setStep(STEPS.SUCCESS);
      setLoading(false);
    } catch (err) {
      console.error('Failed to connect to Datadog:', err);
      setError(err.response?.data?.detail || 'Failed to connect to Datadog. Check your API keys.');
      setLoading(false);
    }
  };

  // Connect to PagerDuty using API token and optional routing key
  const handleSavePagerDutyCredentials = async () => {
    if (!pagerdutyApiToken.trim()) {
      setError('Please enter your PagerDuty API Token');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First validate the token
      const validateRes = await api.post('/pagerduty/validate-token', {
        api_token: pagerdutyApiToken.trim()
      });

      if (!validateRes.data.valid) {
        setError(validateRes.data.error || 'Invalid API token');
        setLoading(false);
        return;
      }

      // Connect with API token and optional routing key
      const response = await api.post('/pagerduty/connect', {
        api_token: pagerdutyApiToken.trim(),
        routing_key: pagerdutyRoutingKey.trim()
      });

      const { connection_id, account, service_count, schedule_count, incident_count, has_routing_key } = response.data;

      // Set connection info
      setConnectionId(connection_id);
      setProvider('pagerduty');
      setConnectionInfo({
        account_name: account.name || account.subdomain,
        subdomain: account.subdomain,
        service_count,
        schedule_count,
        incident_count,
        has_routing_key
      });

      // PagerDuty connected successfully - go to success page
      setStep(STEPS.SUCCESS);
      setLoading(false);
    } catch (err) {
      console.error('Failed to connect to PagerDuty:', err);
      setError(err.response?.data?.detail || 'Failed to connect to PagerDuty. Check your API token.');
      setLoading(false);
    }
  };

  // Save selection and start indexing (works for repos, channels, projects, teams, pipelines, and worker groups)
  const handleConfirmSelection = async () => {
    if (selectedItems.length === 0) {
      const errorMsg = provider === 'github' ? 'Please select at least one repository'
        : provider === 'jira' ? 'Please select at least one project'
          : provider === 'gitlab' ? 'Please select at least one project'
            : provider === 'linear' ? 'Please select at least one team'
              : provider === 'harness' ? 'Please select at least one pipeline'
                : provider === 'cribl' ? 'Please select at least one worker group'
                  : provider === 'buildkite' ? 'Please select at least one pipeline'
                    : 'Please select at least one channel';
      setError(errorMsg);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build request body based on provider
      const requestBody = provider === 'github'
        ? { repo_full_names: selectedItems }
        : provider === 'jira'
          ? { project_keys: selectedItems }
          : provider === 'gitlab'
            ? { project_ids: selectedItems }
            : provider === 'linear'
              ? { team_ids: selectedItems }
              : provider === 'harness'
                ? { pipeline_ids: selectedItems }
                : provider === 'cribl'
                  ? { worker_group_ids: selectedItems }
                  : provider === 'buildkite'
                    ? { pipeline_slugs: selectedItems }
                    : { channel_ids: selectedItems };

      await api.post(`/connect/${connectionId}/selection`, requestBody);

      setStep(STEPS.INDEXING);
      setIndexingStatus({ items: selectedItems.length, synced: 0 });

      // Simulate indexing progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress >= 100) {
          clearInterval(progressInterval);
          setStep(STEPS.SUCCESS);
        } else {
          setIndexingStatus({
            items: selectedItems.length,
            synced: Math.floor((progress / 100) * selectedItems.length)
          });
        }
      }, 500);

      setLoading(false);
    } catch (err) {
      console.error('Failed to save selection:', err);
      setError(err.response?.data?.detail || 'Failed to save selection');
      setLoading(false);
    }
  };

  // Toggle item selection (works for repos and channels)
  const toggleItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(r => r !== itemId)
        : [...prev, itemId]
    );
  };

  // Select/deselect all visible items
  const toggleAllVisible = () => {
    const visibleIds = provider === 'github'
      ? filteredRepos.map(r => r.full_name)
      : provider === 'jira'
        ? filteredProjects.map(p => p.key)
        : provider === 'gitlab'
          ? filteredProjects.map(p => p.id)
          : provider === 'linear'
            ? filteredTeams.map(t => t.id)
            : provider === 'harness'
              ? filteredPipelines.map(p => p.identifier)
              : provider === 'cribl'
                ? workerGroups.map(g => g.id)
                : provider === 'buildkite'
                  ? pipelines.map(p => p.slug)
                  : filteredChannels.map(c => c.id);
    const allSelected = visibleIds.every(id => selectedItems.includes(id));

    if (allSelected) {
      setSelectedItems(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  // Add a public GitHub repo by URL
  const addPublicRepo = async () => {
    if (!publicRepoUrl.trim()) return;

    // Parse GitHub URL to extract owner/repo
    let repoFullName = publicRepoUrl.trim();

    // Handle full GitHub URLs
    const urlMatch = repoFullName.match(/github\.com\/([^\/]+\/[^\/\s]+)/);
    if (urlMatch) {
      repoFullName = urlMatch[1].replace(/\.git$/, '').replace(/\/$/, '');
    }

    // Validate format (owner/repo)
    if (!repoFullName.includes('/') || repoFullName.split('/').length !== 2) {
      setError('Invalid format. Use "owner/repo" or a GitHub URL');
      return;
    }

    // Check if already in list
    if (repos.some(r => r.full_name.toLowerCase() === repoFullName.toLowerCase())) {
      // Just select it
      if (!selectedItems.includes(repoFullName)) {
        setSelectedItems(prev => [...prev, repoFullName]);
      }
      setPublicRepoUrl('');
      return;
    }

    setAddingPublicRepo(true);
    setError(null);

    try {
      // Verify the repo exists and is public
      const response = await fetch(`https://api.github.com/repos/${repoFullName}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Repository not found. Make sure it exists and is public.');
        }
        throw new Error('Failed to verify repository');
      }

      const repoData = await response.json();

      // Add to repos list
      const newRepo = {
        id: repoData.id,
        full_name: repoData.full_name,
        name: repoData.name,
        owner: repoData.owner.login,
        private: repoData.private,
        default_branch: repoData.default_branch,
        description: repoData.description,
        language: repoData.language,
        stargazers_count: repoData.stargazers_count,
        open_issues_count: repoData.open_issues_count,
        pushed_at: repoData.pushed_at,
        is_public_added: true  // Mark as manually added
      };

      setRepos(prev => [newRepo, ...prev]);
      setSelectedItems(prev => [...prev, repoData.full_name]);
      setPublicRepoUrl('');

      // Update orgs list if new owner
      if (!orgs.some(o => o.login === repoData.owner.login)) {
        setOrgs(prev => [...prev, { login: repoData.owner.login }]);
      }
    } catch (err) {
      console.error('Failed to add public repo:', err);
      setError(err.message || 'Failed to add repository');
    } finally {
      setAddingPublicRepo(false);
    }
  };

  // Filter repos (GitHub)
  const filteredRepos = repos.filter(repo => {
    const matchesSearch = !searchQuery ||
      repo.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (repo.description && repo.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesOrg = selectedOrg === 'all' || repo.owner === selectedOrg;

    return matchesSearch && matchesOrg;
  });

  // Filter channels (Slack)
  const filteredChannels = channels.filter(channel => {
    const matchesSearch = !searchQuery ||
      channel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (channel.topic && channel.topic.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (channel.purpose && channel.purpose.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesSearch;
  });

  // Filter projects (Jira and GitLab)
  const filteredProjects = projects.filter(project => {
    const matchesSearch = !searchQuery ||
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.key && project.key.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (project.path_with_namespace && project.path_with_namespace.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesSearch;
  });

  // Filter teams (Linear)
  const filteredTeams = teams.filter(team => {
    const matchesSearch = !searchQuery ||
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (team.description && team.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesSearch;
  });

  // Filter pipelines (Harness)
  const filteredPipelines = pipelines.filter(pipeline => {
    const matchesSearch = !searchQuery ||
      pipeline.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pipeline.identifier.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (pipeline.description && pipeline.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesSearch;
  });

  // Get unique owners for org filter
  const uniqueOwners = [...new Set(repos.map(r => r.owner))];

  if (!isOpen) return null;

  const getStepTitle = () => {
    switch (step) {
      case STEPS.SOURCE_PICKER: return 'Connect your tools';
      case STEPS.CREDENTIALS_SETUP: return `Set up ${selectedSource?.name}`;
      case STEPS.OAUTH_PENDING: return `Connecting to ${selectedSource?.name}`;
      case STEPS.SELECTION:
        if (mode === 'manage') {
          return provider === 'github' ? 'Manage repositories'
            : (provider === 'jira' || provider === 'gitlab') ? 'Manage projects'
              : provider === 'linear' ? 'Manage teams'
                : provider === 'harness' ? 'Manage pipelines'
                  : 'Manage channels';
        }
        return provider === 'github' ? 'Choose repositories to sync'
          : (provider === 'jira' || provider === 'gitlab') ? 'Choose projects to sync'
            : provider === 'linear' ? 'Choose teams to sync'
              : provider === 'harness' ? 'Choose pipelines to sync'
                : 'Choose channels to monitor';
      case STEPS.INDEXING: return 'Updating your data';
      case STEPS.SUCCESS: return mode === 'manage' ? 'Updated!' : 'Connected!';
      case STEPS.ERROR: return 'Something went wrong';
      default: return mode === 'manage' ? 'Manage' : 'Connect';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {step !== STEPS.SOURCE_PICKER && step !== STEPS.SUCCESS && (
              <button
                onClick={() => setStep(STEPS.SOURCE_PICKER)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{getStepTitle()}</h2>
              {step === STEPS.SOURCE_PICKER && (
                <p className="text-sm text-gray-500">
                  Vedaya uses read-only access to answer questions with citations. You choose what we sync.
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step: Source Picker */}
          {step === STEPS.SOURCE_PICKER && (
            <div className="space-y-3">
              {SOURCES.map((source) => {
                const Icon = source.icon;
                const isOneClick = source.oneClick;
                const isConnected = connectedSources[source.id];
                return (
                  <button
                    key={source.id}
                    onClick={() => !isConnected && handleSourceClick(source)}
                    disabled={!source.available || loading || isConnected}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${isConnected
                        ? 'border-green-200 bg-green-50/50 cursor-default'
                        : source.available
                          ? isOneClick
                            ? 'border-gray-900 hover:bg-gray-50 cursor-pointer ring-1 ring-gray-900/5'
                            : source.apiKey
                              ? 'border-cyan-200 hover:border-cyan-400 hover:bg-cyan-50/50 cursor-pointer'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer'
                          : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                      }`}
                  >
                    <div className={`p-3 rounded-xl ${source.bgColor} ${source.textColor}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{source.name}</span>
                        {isConnected ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Connected
                          </span>
                        ) : source.oneClick ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Instant
                          </span>
                        ) : source.apiKey ? (
                          <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <Key className="w-3 h-3" />
                            API Key
                          </span>
                        ) : null}
                        {source.comingSoon && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                            Coming soon
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {source.tagline || source.description}
                      </p>
                    </div>
                    {source.available && !loading && (
                      isConnected ? (
                        <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm font-medium rounded-lg flex items-center gap-1.5">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                      ) : isOneClick ? (
                        <span className="px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg">
                          Connect
                        </span>
                      ) : source.apiKey ? (
                        <span className="px-3 py-1.5 bg-cyan-600 text-white text-sm font-medium rounded-lg">
                          Connect
                        </span>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )
                    )}
                    {loading && selectedSource?.id === source.id && (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    )}
                  </button>
                );
              })}

              {/* Trust footer */}
              <div className="pt-6 mt-6 border-t border-gray-100">
                <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    Read-only
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" />
                    Encrypted
                  </span>
                  <span className="flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" />
                    Revoke anytime
                  </span>
                </div>
                <div className="flex justify-center gap-4 mt-3 text-xs">
                  <a href="#" className="text-blue-600 hover:underline">Security</a>
                  <a href="#" className="text-blue-600 hover:underline">Privacy</a>
                </div>
              </div>
            </div>
          )}

          {/* Step: Credentials Setup */}
          {step === STEPS.CREDENTIALS_SETUP && selectedSource && (
            <div className="space-y-6">
              {/* Provider header */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className={`p-2.5 rounded-xl ${selectedSource.bgColor} ${selectedSource.textColor}`}>
                  {selectedSource.icon && <selectedSource.icon className="w-6 h-6" />}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {selectedSource.id === 'github' ? 'GitHub Setup Required' : `${selectedSource.name} Setup`}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedSource.id === 'github'
                      ? 'One-time configuration for your organization'
                      : `Configure your ${selectedSource.name} app credentials`
                    }
                  </p>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* GitHub Credentials Form - Admin only (self-hosted) */}
              {selectedSource.id === 'github' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Admin Configuration Required</p>
                        <p className="text-sm text-amber-700 mt-1">
                          GitHub OAuth needs to be configured by an administrator. This is typically only needed for self-hosted deployments.
                        </p>
                      </div>
                    </div>
                  </div>

                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                      Configure OAuth App (Admin)
                    </summary>
                    <div className="mt-4 pl-6 space-y-4">
                      <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                        <p className="font-medium mb-2">Callback URL for your GitHub OAuth App:</p>
                        <code className="bg-white px-2 py-1 rounded border text-xs break-all">
                          https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/github
                        </code>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                          <input
                            type="text"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ov23li..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                          <input
                            type="password"
                            value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="••••••••••••••••"
                          />
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              )}

              {/* Slack Credentials Form */}
              {selectedSource.id === 'slack' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">OAuth Credentials</h4>
                    <a
                      href="https://api.slack.com/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      Create Slack App
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={slackClientId}
                        onChange={(e) => setSlackClientId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1234567890.1234567890123"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Secret <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={slackClientSecret}
                          onChange={(e) => setSlackClientSecret(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Signing Secret <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={showSecrets ? "text" : "password"}
                        value={slackSigningSecret}
                        onChange={(e) => setSlackSigningSecret(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="••••••••••••••••"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Redirect URL <span className="text-red-500">*</span>
                        </label>
                        {slackRedirectUrl && <CopyButton text={slackRedirectUrl} />}
                      </div>
                      <input
                        type="text"
                        value={slackRedirectUrl}
                        onChange={(e) => setSlackRedirectUrl(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/slack"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add this URL to your Slack app's OAuth Redirect URLs.
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-purple-900 mb-2">Required Scopes</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['channels:read', 'channels:history', 'groups:read', 'groups:history', 'users:read', 'chat:write'].map(scope => (
                        <span key={scope} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Jira Credentials Form */}
              {selectedSource?.id === 'jira' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">OAuth 2.0 Credentials</h4>
                    <a
                      href="https://developer.atlassian.com/console/myapps/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      Create Jira App
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={jiraClientId}
                        onChange={(e) => setJiraClientId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your Jira Client ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Secret <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={jiraClientSecret}
                          onChange={(e) => setJiraClientSecret(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-blue-900 mb-2">Required Scopes</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['read:jira-user', 'read:jira-work', 'offline_access'].map(scope => (
                        <span key={scope} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">Callback URL for your Jira OAuth App:</p>
                    <code className="bg-white px-2 py-1 rounded border text-xs break-all">
                      https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/jira
                    </code>
                  </div>
                </div>
              )}

              {/* GitLab Credentials Form */}
              {selectedSource?.id === 'gitlab' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">OAuth 2.0 Credentials</h4>
                    <a
                      href="https://gitlab.com/-/profile/applications"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                    >
                      Create GitLab App
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GitLab URL
                      </label>
                      <input
                        type="text"
                        value={gitlabUrl}
                        onChange={(e) => setGitlabUrl(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://gitlab.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use https://gitlab.com for GitLab.com or your self-hosted URL
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Application ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={gitlabClientId}
                        onChange={(e) => setGitlabClientId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter your GitLab Application ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Secret <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={gitlabClientSecret}
                          onChange={(e) => setGitlabClientSecret(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="••••••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-orange-900 mb-2">Required Scopes</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['api', 'read_user', 'read_api', 'read_repository'].map(scope => (
                        <span key={scope} className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">Redirect URI for your GitLab OAuth App:</p>
                    <code className="bg-white px-2 py-1 rounded border text-xs break-all">
                      https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/gitlab
                    </code>
                    <p className="text-xs text-orange-700 mt-2">
                      GitLab OAuth provides access to projects, merge requests, issues, and pipelines.
                    </p>
                  </div>
                </div>
              )}

              {/* Linear Credentials Form */}
              {selectedSource?.id === 'linear' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">OAuth 2.0 Credentials</h4>
                    <a
                      href="https://linear.app/settings/api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-violet-600 hover:text-violet-700 flex items-center gap-1"
                    >
                      Create Linear App
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={linearClientId}
                        onChange={(e) => setLinearClientId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="Enter your Linear Client ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Secret <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={linearClientSecret}
                          onChange={(e) => setLinearClientSecret(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
                          placeholder="••••••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-violet-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-violet-900 mb-2">Required Scope</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['read'].map(scope => (
                        <span key={scope} className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">Callback URL for your Linear OAuth App:</p>
                    <code className="bg-white px-2 py-1 rounded border text-xs break-all">
                      https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/linear
                    </code>
                    <p className="text-xs text-violet-700 mt-2">
                      Linear's OAuth provides read access to teams, issues, projects, and cycles.
                    </p>
                  </div>
                </div>
              )}

              {/* Harness Credentials Form (API Key) */}
              {selectedSource?.id === 'harness' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">API Key Authentication</h4>
                    <a
                      href="https://developer.harness.io/docs/platform/automation/api/add-and-manage-api-keys/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                    >
                      Get API Key
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Key className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-cyan-800">API Key Required</p>
                        <p className="text-sm text-cyan-700 mt-1">
                          Harness uses Personal Access Tokens (PAT) for API authentication. Create one in your Harness account settings.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={harnessAccountId}
                        onChange={(e) => setHarnessAccountId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="Enter your Harness Account ID"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Found in your Harness URL: app.harness.io/ng/account/<strong>ACCOUNT_ID</strong>/...
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key (PAT) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={harnessApiKey}
                          onChange={(e) => setHarnessApiKey(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          placeholder="pat.xxxxx.xxxxxx.xxxxxxxxxx"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Base URL
                      </label>
                      <input
                        type="text"
                        value={harnessBaseUrl}
                        onChange={(e) => setHarnessBaseUrl(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="https://app.harness.io"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use https://app.harness.io for Harness SaaS or your self-hosted URL
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization Identifier <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={harnessOrgId}
                        onChange={(e) => setHarnessOrgId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="default"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The organization identifier (e.g., "default" or your org name)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Identifier <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={harnessProjectId}
                        onChange={(e) => setHarnessProjectId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="my-project"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The project identifier containing your pipelines
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">What Vedaya will access:</p>
                    <ul className="space-y-1 text-gray-700">
                      <li className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        Pipeline configurations and metadata
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        Pipeline execution history and logs
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        Deployment status and metrics
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Cribl Credentials Form */}
              {selectedSource?.id === 'cribl' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">API Credentials</h4>
                    <a
                      href="https://docs.cribl.io/stream/api-tutorials/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                    >
                      Cribl API Docs
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={criblClientId}
                        onChange={(e) => setCriblClientId(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Enter your Cribl Client ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Secret <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={criblClientSecret}
                          onChange={(e) => setCriblClientSecret(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="••••••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cribl URL <span className="text-gray-400">(optional, for self-hosted)</span>
                      </label>
                      <input
                        type="text"
                        value={criblUrl}
                        onChange={(e) => setCriblUrl(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="https://cloud.cribl.io"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty for Cribl Cloud. For self-hosted, enter your instance URL.
                      </p>
                    </div>
                  </div>

                  <div className="bg-teal-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-teal-900 mb-2">How to get API credentials</h5>
                    <ol className="text-xs text-teal-800 space-y-1 list-decimal list-inside">
                      <li>Go to Cribl Cloud → Settings → Access Management → API Credentials</li>
                      <li>Click "Add Credentials" to create a new Client ID and Secret</li>
                      <li>Copy the credentials and paste them above</li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="text-teal-700">
                      <strong>Note:</strong> Your credentials are exchanged for a 24-hour bearer token.
                      We'll automatically refresh it when needed.
                    </p>
                  </div>
                </div>
              )}

              {/* Buildkite API Token Form */}
              {selectedSource?.id === 'buildkite' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">API Token</h4>
                    <a
                      href="https://buildkite.com/user/api-access-tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                    >
                      Get API Token
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Token <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={buildkiteApiToken}
                          onChange={(e) => setBuildkiteApiToken(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="bkua_xxxxxxxxxxxxxxxxxxxx"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {buildkiteOrganizations.length > 0 ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Organization <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={buildkiteOrgSlug}
                          onChange={(e) => setBuildkiteOrgSlug(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Select an organization</option>
                          {buildkiteOrganizations.map(org => (
                            <option key={org.slug} value={org.slug}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : buildkiteOrgSlug && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Organization Slug <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={buildkiteOrgSlug}
                          onChange={(e) => setBuildkiteOrgSlug(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="my-organization"
                        />
                      </div>
                    )}
                  </div>

                  <div className="bg-emerald-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-emerald-900 mb-2">Required Token Scopes</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['read_builds', 'read_pipelines', 'read_agents'].map(scope => (
                        <span key={scope} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-emerald-700 mt-2">
                      Create an API token at Buildkite → User Settings → API Access Tokens
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="text-emerald-700">
                      <strong>Tip:</strong> Enter your token to auto-detect your organizations. Then select one to connect.
                    </p>
                  </div>
                </div>
              )}

              {/* Datadog API Keys Form */}
              {selectedSource?.id === 'datadog' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">API Keys</h4>
                    <a
                      href="https://app.datadoghq.com/organization-settings/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      Get API Keys
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Datadog Site <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={datadogSite}
                        onChange={(e) => setDatadogSite(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="datadoghq.com">US1 (datadoghq.com)</option>
                        <option value="us3.datadoghq.com">US3 (us3.datadoghq.com)</option>
                        <option value="us5.datadoghq.com">US5 (us5.datadoghq.com)</option>
                        <option value="datadoghq.eu">EU (datadoghq.eu)</option>
                        <option value="ap1.datadoghq.com">AP1 (ap1.datadoghq.com)</option>
                        <option value="ddog-gov.com">US1-FED (ddog-gov.com)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select your Datadog region. Check your browser URL to identify your site.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={datadogApiKey}
                          onChange={(e) => setDatadogApiKey(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Enter your Datadog API Key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Found in Organization Settings → API Keys
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Application Key <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={datadogAppKey}
                          onChange={(e) => setDatadogAppKey(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Enter your Datadog Application Key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Found in Organization Settings → Application Keys (needed for reading data)
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-purple-900 mb-2">What Vedaya will access</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['Monitors', 'Dashboards', 'Events', 'Incidents', 'SLOs'].map(item => (
                        <span key={item} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                          {item}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-purple-700 mt-2">
                      Vedaya uses read-only access to help with incident investigation and monitoring insights.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">How to create keys:</p>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Organization Settings → API Keys → New Key</li>
                      <li>Go to Organization Settings → Application Keys → New Key</li>
                      <li>Copy both keys and paste them above</li>
                    </ol>
                  </div>
                </div>
              )}

              {/* PagerDuty API Token Form */}
              {selectedSource?.id === 'pagerduty' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">API Credentials</h4>
                    <a
                      href="https://support.pagerduty.com/docs/api-access-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                    >
                      Get API Token
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Token <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={pagerdutyApiToken}
                          onChange={(e) => setPagerdutyApiToken(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Enter your PagerDuty API Token"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Create a General Access REST API Key in PagerDuty → Integrations → API Access Keys
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Routing Key (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets ? "text" : "password"}
                          value={pagerdutyRoutingKey}
                          onChange={(e) => setPagerdutyRoutingKey(e.target.value)}
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Enter your Events API v2 Integration Key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(!showSecrets)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Required to trigger incidents from Vedaya. Found in Service → Integrations → Events API V2
                      </p>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3">
                    <h5 className="text-sm font-medium text-green-900 mb-2">What Vedaya will access</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {['Services', 'Schedules', 'On-Calls', 'Incidents', 'Escalation Policies'].map(item => (
                        <span key={item} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                          {item}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-green-700 mt-2">
                      Vedaya imports your on-call schedules and incidents to help with context during investigations.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-2">How to create an API token:</p>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Integrations → API Access Keys</li>
                      <li>Click "Create New API Key"</li>
                      <li>Select "General Access REST API Key"</li>
                      <li>Copy the key and paste it above</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: OAuth Pending */}
          {step === STEPS.OAUTH_PENDING && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`w-20 h-20 ${selectedSource?.bgColor} rounded-2xl flex items-center justify-center mb-6 ${selectedSource?.textColor} shadow-lg`}>
                {selectedSource?.icon && <selectedSource.icon className="w-12 h-12" />}
              </div>

              {/* Animated connection indicator */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-600">Connecting securely...</span>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {provider === 'github' ? 'Authorize Vedaya on GitHub' : `Connect to ${selectedSource?.name}`}
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                {provider === 'github'
                  ? "Just click 'Authorize' in the popup. We only request read access to help answer your questions."
                  : "Complete the authorization in the popup window."
                }
              </p>

              {/* Permission preview for GitHub */}
              {provider === 'github' && (
                <div className="bg-gray-50 rounded-xl p-4 w-full max-w-sm mb-4">
                  <p className="text-xs font-medium text-gray-600 mb-3">Vedaya will be able to:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read your repositories & code</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read pull requests & issues</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read commit history</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Read-only access. We never modify your code.
                  </p>
                </div>
              )}

              {/* Permission preview for GitLab */}
              {provider === 'gitlab' && (
                <div className="bg-orange-50 rounded-xl p-4 w-full max-w-sm mb-4">
                  <p className="text-xs font-medium text-orange-800 mb-3">Vedaya will be able to:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read your projects & code</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read merge requests & issues</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read CI/CD pipelines & jobs</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Read commit history</span>
                    </div>
                  </div>
                  <p className="text-xs text-orange-700 mt-3 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Read-only access. We never modify your code.
                  </p>
                </div>
              )}

              <button
                onClick={() => setStep(STEPS.SOURCE_PICKER)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Step: Selection (works for GitHub repos, Slack channels, and Jira projects) */}
          {step === STEPS.SELECTION && (
            <div className="space-y-4">
              {/* Connection info */}
              {connectionInfo && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {provider === 'github' ? (
                    connectionInfo.avatar_url ? (
                      <img src={connectionInfo.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <GitHubLogo className="w-4 h-4 text-gray-500" />
                      </div>
                    )
                  ) : provider === 'jira' ? (
                    connectionInfo.avatar_url ? (
                      <img src={connectionInfo.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <JiraLogo className="w-5 h-5" />
                      </div>
                    )
                  ) : provider === 'gitlab' ? (
                    connectionInfo.avatar_url ? (
                      <img src={connectionInfo.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <GitLabLogo className="w-5 h-5" />
                      </div>
                    )
                  ) : provider === 'linear' ? (
                    connectionInfo.avatar_url ? (
                      <img src={connectionInfo.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                        <LinearLogo className="w-5 h-5" />
                      </div>
                    )
                  ) : provider === 'harness' ? (
                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                      <HarnessLogo className="w-5 h-5" />
                    </div>
                  ) : provider === 'cribl' ? (
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                      <CriblLogo className="w-5 h-5" />
                    </div>
                  ) : provider === 'buildkite' ? (
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <BuildkiteLogo className="w-5 h-5" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <SlackLogo className="w-5 h-5" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {provider === 'github' ? (
                        <>Connected as <span className="text-blue-600">@{connectionInfo.login}</span></>
                      ) : provider === 'jira' ? (
                        <>Connected to <span className="text-blue-600">{connectionInfo.display_name || connectionInfo.site_url}</span></>
                      ) : provider === 'gitlab' ? (
                        <>Connected as <span className="text-orange-600">@{connectionInfo.username}</span></>
                      ) : provider === 'linear' ? (
                        <>Connected to <span className="text-violet-600">{connectionInfo.organization_name}</span></>
                      ) : provider === 'harness' ? (
                        <>Connected to <span className="text-cyan-600">{connectionInfo.account_name || connectionInfo.account_identifier}</span></>
                      ) : provider === 'cribl' ? (
                        <>Connected to <span className="text-teal-600">{connectionInfo.organization_name || 'Cribl Cloud'}</span></>
                      ) : provider === 'buildkite' ? (
                        <>Connected to <span className="text-emerald-600">{connectionInfo.organization_name || connectionInfo.organization_slug}</span></>
                      ) : (
                        <>Connected to <span className="text-purple-600">{connectionInfo.team_name}</span></>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedItems.length} of {
                        provider === 'github' ? repos.length :
                          (provider === 'jira' || provider === 'gitlab') ? projects.length :
                            provider === 'linear' ? teams.length :
                              provider === 'harness' ? pipelines.length :
                                provider === 'cribl' ? workerGroups.length :
                                  provider === 'buildkite' ? pipelines.length :
                                    channels.length
                      } {
                        provider === 'github' ? 'repositories' :
                          (provider === 'jira' || provider === 'gitlab') ? 'projects' :
                            provider === 'linear' ? 'teams' :
                              provider === 'harness' ? 'pipelines' :
                                provider === 'cribl' ? 'worker groups' :
                                  provider === 'buildkite' ? 'pipelines' :
                                    'channels'
                      } selected
                    </p>
                  </div>
                </div>
              )}

              {/* Search and filters */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                      provider === 'github' ? 'Search repositories...' :
                        (provider === 'jira' || provider === 'gitlab') ? 'Search projects...' :
                          provider === 'linear' ? 'Search teams...' :
                            provider === 'harness' ? 'Search pipelines...' :
                              provider === 'cribl' ? 'Search worker groups...' :
                                provider === 'buildkite' ? 'Search pipelines...' :
                                  'Search channels...'
                    }
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {provider === 'github' && uniqueOwners.length > 1 && (
                  <select
                    value={selectedOrg}
                    onChange={(e) => setSelectedOrg(e.target.value)}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All owners</option>
                    {uniqueOwners.map(owner => (
                      <option key={owner} value={owner}>{owner}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Add public repo (GitHub only) */}
              {provider === 'github' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Add Public Repository</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={publicRepoUrl}
                      onChange={(e) => setPublicRepoUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !addingPublicRepo && addPublicRepo()}
                      placeholder="owner/repo or https://github.com/owner/repo"
                      className="flex-1 px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
                      disabled={addingPublicRepo}
                    />
                    <button
                      onClick={addPublicRepo}
                      disabled={!publicRepoUrl.trim() || addingPublicRepo}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      {addingPublicRepo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Add'
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Add any public GitHub repository to sync
                  </p>
                </div>
              )}

              {/* Select all toggle */}
              <div className="flex items-center justify-between px-1">
                <button
                  onClick={toggleAllVisible}
                  className={`text-sm ${provider === 'gitlab' ? 'text-orange-600 hover:text-orange-700' :
                      provider === 'linear' ? 'text-violet-600 hover:text-violet-700' :
                        provider === 'harness' ? 'text-cyan-600 hover:text-cyan-700' :
                          provider === 'cribl' ? 'text-teal-600 hover:text-teal-700' :
                            provider === 'buildkite' ? 'text-emerald-600 hover:text-emerald-700' :
                              'text-blue-600 hover:text-blue-700'
                    } font-medium`}
                >
                  {(provider === 'github'
                    ? filteredRepos.every(r => selectedItems.includes(r.full_name))
                    : provider === 'jira'
                      ? filteredProjects.every(p => selectedItems.includes(p.key))
                      : provider === 'gitlab'
                        ? filteredProjects.every(p => selectedItems.includes(p.id))
                        : provider === 'linear'
                          ? filteredTeams.every(t => selectedItems.includes(t.id))
                          : provider === 'harness'
                            ? filteredPipelines.every(p => selectedItems.includes(p.identifier))
                            : provider === 'cribl'
                              ? workerGroups.every(g => selectedItems.includes(g.id))
                              : provider === 'buildkite'
                                ? pipelines.every(p => selectedItems.includes(p.slug))
                                : filteredChannels.every(c => selectedItems.includes(c.id))
                  ) ? 'Deselect all' : 'Select all visible'}
                </button>
                <span className="text-xs text-gray-500">
                  {provider === 'github' ? filteredRepos.length
                    : provider === 'jira' ? filteredProjects.length
                      : provider === 'gitlab' ? filteredProjects.length
                        : provider === 'linear' ? filteredTeams.length
                          : provider === 'harness' ? filteredPipelines.length
                            : provider === 'cribl' ? workerGroups.length
                              : provider === 'buildkite' ? pipelines.length
                                : filteredChannels.length} {
                    provider === 'github' ? 'repositories'
                      : provider === 'jira' ? 'projects'
                        : provider === 'gitlab' ? 'projects'
                          : provider === 'linear' ? 'teams'
                            : provider === 'harness' ? 'pipelines'
                              : provider === 'cribl' ? 'worker groups'
                                : provider === 'buildkite' ? 'pipelines'
                                  : 'channels'
                  }
                </span>
              </div>

              {/* Item list */}
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  </div>
                ) : provider === 'github' ? (
                  /* GitHub Repos List */
                  filteredRepos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Database className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No repositories found</p>
                    </div>
                  ) : (
                    filteredRepos.map((repo) => (
                      <label
                        key={repo.full_name}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(repo.full_name)}
                          onChange={() => toggleItem(repo.full_name)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{repo.name}</span>
                            {repo.private && (
                              <Lock className="w-3.5 h-3.5 text-gray-400" />
                            )}
                            {repo.is_public_added && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                Public
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{repo.owner}</span>
                            {repo.language && <span>{repo.language}</span>}
                            {repo.pushed_at && (
                              <span>
                                Updated {new Date(repo.pushed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {repo.open_issues_count > 0 && (
                          <span className="text-xs text-gray-400">
                            {repo.open_issues_count} issues
                          </span>
                        )}
                      </label>
                    ))
                  )
                ) : provider === 'jira' ? (
                  /* Jira Projects List */
                  filteredProjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Layers className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No projects found</p>
                    </div>
                  ) : (
                    filteredProjects.map((project) => (
                      <label
                        key={project.key}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(project.key)}
                          onChange={() => toggleItem(project.key)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {project.avatar_url && (
                              <img src={project.avatar_url} alt="" className="w-5 h-5 rounded" />
                            )}
                            <span className="font-medium text-gray-900">{project.name}</span>
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                              {project.key}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="capitalize">{project.project_type}</span>
                            {project.lead && <span>Lead: {project.lead}</span>}
                            {project.description && (
                              <span className="truncate max-w-xs">{project.description}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )
                ) : provider === 'gitlab' ? (
                  /* GitLab Projects List */
                  filteredProjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <GitLabLogo className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No projects found</p>
                    </div>
                  ) : (
                    filteredProjects.map((project) => (
                      <label
                        key={project.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(project.id)}
                          onChange={() => toggleItem(project.id)}
                          className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {project.avatar_url && (
                              <img src={project.avatar_url} alt="" className="w-5 h-5 rounded" />
                            )}
                            <span className="font-medium text-gray-900">{project.name}</span>
                            {project.visibility === 'private' && (
                              <Lock className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{project.path_with_namespace}</span>
                            {project.default_branch && <span>{project.default_branch}</span>}
                            {project.last_activity_at && (
                              <span>
                                Updated {new Date(project.last_activity_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {project.visibility && (
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${project.visibility === 'public' ? 'bg-green-100 text-green-700' :
                              project.visibility === 'internal' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                            }`}>
                            {project.visibility}
                          </span>
                        )}
                      </label>
                    ))
                  )
                ) : provider === 'linear' ? (
                  /* Linear Teams List */
                  filteredTeams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <Layers className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No teams found</p>
                    </div>
                  ) : (
                    filteredTeams.map((team) => (
                      <label
                        key={team.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(team.id)}
                          onChange={() => toggleItem(team.id)}
                          className="w-4 h-4 text-violet-600 rounded border-gray-300 focus:ring-violet-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {team.icon && (
                              <span className="text-lg">{team.icon}</span>
                            )}
                            <span className="font-medium text-gray-900">{team.name}</span>
                            <span className="px-1.5 py-0.5 bg-violet-100 text-violet-600 text-xs font-medium rounded">
                              {team.key}
                            </span>
                            {team.private && (
                              <Lock className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {team.issue_count > 0 && <span>{team.issue_count} issues</span>}
                            {team.member_count > 0 && <span>{team.member_count} members</span>}
                            {team.description && (
                              <span className="truncate max-w-xs">{team.description}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )
                ) : provider === 'harness' ? (
                  /* Harness Pipelines List */
                  filteredPipelines.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <HarnessLogo className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No pipelines found</p>
                    </div>
                  ) : (
                    filteredPipelines.map((pipeline) => (
                      <label
                        key={pipeline.identifier}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(pipeline.identifier)}
                          onChange={() => toggleItem(pipeline.identifier)}
                          className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{pipeline.name}</span>
                            <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-600 text-xs font-medium rounded">
                              {pipeline.identifier}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {pipeline.project_identifier && <span>Project: {pipeline.project_identifier}</span>}
                            {pipeline.org_identifier && <span>Org: {pipeline.org_identifier}</span>}
                            {pipeline.description && (
                              <span className="truncate max-w-xs">{pipeline.description}</span>
                            )}
                          </div>
                        </div>
                        {pipeline.last_execution_status && (
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${pipeline.last_execution_status === 'Success' ? 'bg-green-100 text-green-700' :
                              pipeline.last_execution_status === 'Failed' ? 'bg-red-100 text-red-700' :
                                pipeline.last_execution_status === 'Running' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                            }`}>
                            {pipeline.last_execution_status}
                          </span>
                        )}
                      </label>
                    ))
                  )
                ) : provider === 'cribl' ? (
                  /* Cribl Worker Groups List */
                  workerGroups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <CriblLogo className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No worker groups found</p>
                    </div>
                  ) : (
                    workerGroups.filter(g =>
                      !searchQuery ||
                      g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      g.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      g.description?.toLowerCase().includes(searchQuery.toLowerCase())
                    ).map((group) => (
                      <label
                        key={group.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(group.id)}
                          onChange={() => toggleItem(group.id)}
                          className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <CriblLogo className="w-5 h-5" />
                            <span className="font-medium text-gray-900">{group.name}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${group.status === 'healthy' ? 'bg-green-100 text-green-700' :
                                group.status === 'degraded' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                              }`}>
                              {group.status || 'unknown'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{group.worker_count || 0} workers</span>
                            {group.config_version && <span>v{group.config_version}</span>}
                            {group.description && (
                              <span className="truncate max-w-xs">{group.description}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )
                ) : provider === 'buildkite' ? (
                  /* Buildkite Pipelines List */
                  pipelines.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <BuildkiteLogo className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No pipelines found</p>
                    </div>
                  ) : (
                    pipelines.filter(p =>
                      !searchQuery ||
                      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
                    ).map((pipeline) => (
                      <label
                        key={pipeline.slug}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(pipeline.slug)}
                          onChange={() => toggleItem(pipeline.slug)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <BuildkiteLogo className="w-5 h-5" />
                            <span className="font-medium text-gray-900">{pipeline.name}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${pipeline.running_builds_count > 0 ? 'bg-yellow-100 text-yellow-700' :
                                pipeline.scheduled_builds_count > 0 ? 'bg-blue-100 text-blue-700' :
                                  'bg-green-100 text-green-700'
                              }`}>
                              {pipeline.running_builds_count > 0 ? 'running' :
                                pipeline.scheduled_builds_count > 0 ? 'scheduled' : 'idle'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="font-mono">{pipeline.slug}</span>
                            {pipeline.builds_count > 0 && <span>{pipeline.builds_count} builds</span>}
                            {pipeline.description && (
                              <span className="truncate max-w-xs">{pipeline.description}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )
                ) : (
                  /* Slack Channels List */
                  filteredChannels.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <MessageSquare className="w-8 h-8 mb-2 text-gray-300" />
                      <p className="text-sm">No channels found</p>
                    </div>
                  ) : (
                    filteredChannels.map((channel) => (
                      <label
                        key={channel.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(channel.id)}
                          onChange={() => toggleItem(channel.id)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">#{channel.name}</span>
                            {channel.is_private && (
                              <Lock className="w-3.5 h-3.5 text-gray-400" />
                            )}
                            {(channel.is_ext_shared || channel.is_shared) && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                                Slack Connect
                              </span>
                            )}
                            {channel.is_incident_channel && (
                              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                                Incident
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {channel.num_members > 0 && <span>{channel.num_members} members</span>}
                            {channel.purpose && (
                              <span className="truncate max-w-xs">{channel.purpose}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Indexing */}
          {step === STEPS.INDEXING && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className={`w-16 h-16 ${provider === 'github' ? 'bg-blue-100' : provider === 'jira' ? 'bg-blue-100' : 'bg-purple-100'} rounded-2xl flex items-center justify-center mb-6 relative`}>
                {provider === 'github' ? (
                  <Database className="w-8 h-8 text-blue-600" />
                ) : provider === 'jira' ? (
                  <Layers className="w-8 h-8 text-blue-600" />
                ) : (
                  <MessageSquare className="w-8 h-8 text-purple-600" />
                )}
                <div className={`absolute -top-1 -right-1 w-5 h-5 ${provider === 'github' || provider === 'jira' ? 'bg-blue-500' : 'bg-purple-500'} rounded-full flex items-center justify-center`}>
                  <Loader2 className="w-3 h-3 text-white animate-spin" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {provider === 'github' ? 'Indexing your repositories' : provider === 'jira' ? 'Syncing your projects' : 'Syncing your channels'}
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                {provider === 'github'
                  ? "We're syncing your selected repositories. This happens in the background - you can close this and check back later."
                  : provider === 'jira'
                    ? "We're syncing issues from your selected projects. This happens in the background - you can close this and check back later."
                    : "We're syncing threads from your selected channels. This happens in the background - you can close this and check back later."
                }
              </p>

              {/* Progress stats */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-gray-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{indexingStatus.items}</p>
                  <p className="text-xs text-gray-500">{provider === 'github' ? 'Repos' : 'Channels'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{indexingStatus.synced}</p>
                  <p className="text-xs text-gray-500">Synced</p>
                </div>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === STEPS.SUCCESS && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedSource?.name} connected!
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                Your {selectedSource?.id === 'github' ? 'repositories are being indexed' : selectedSource?.id === 'harness' ? 'pipelines are being synced' : 'workspace is connected'}. You can now ask Vedaya questions about your data.
              </p>

              {/* Example questions */}
              <div className={`w-full ${selectedSource?.id === 'harness' ? 'bg-cyan-50' : 'bg-blue-50'} rounded-xl p-4 mb-4`}>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className={`w-4 h-4 ${selectedSource?.id === 'harness' ? 'text-cyan-600' : 'text-blue-600'}`} />
                  <p className={`text-sm font-medium ${selectedSource?.id === 'harness' ? 'text-cyan-900' : 'text-blue-900'}`}>Now you can ask Vedaya...</p>
                </div>
                <div className="space-y-2">
                  {selectedSource?.id === 'github' ? (
                    <>
                      <p className="text-sm text-blue-700">"Show me PRs related to latency spikes"</p>
                      <p className="text-sm text-blue-700">"What changed in the last week that affected deployments?"</p>
                      <p className="text-sm text-blue-700">"Who worked on the authentication module?"</p>
                    </>
                  ) : selectedSource?.id === 'harness' ? (
                    <>
                      <p className="text-sm text-cyan-700">"Show me failed deployments from last week"</p>
                      <p className="text-sm text-cyan-700">"What pipelines ran during the incident?"</p>
                      <p className="text-sm text-cyan-700">"Which deployment caused the production issue?"</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-blue-700">"What happened during yesterday's incident?"</p>
                      <p className="text-sm text-blue-700">"Show me discussions about the API timeout"</p>
                      <p className="text-sm text-blue-700">"Who was involved in the production fix?"</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step: Error */}
          {step === STEPS.ERROR && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Connection failed
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                {error || 'Something went wrong while connecting.'}
              </p>
              <button
                onClick={() => {
                  setError(null);
                  setStep(STEPS.SOURCE_PICKER);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === STEPS.CREDENTIALS_SETUP && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setStep(STEPS.SOURCE_PICKER)}
              className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 font-medium rounded-lg transition-colors"
            >
              Back
            </button>
            <button
              onClick={
                selectedSource?.id === 'github' ? handleSaveGitHubCredentials
                  : selectedSource?.id === 'jira' ? handleSaveJiraCredentials
                    : selectedSource?.id === 'gitlab' ? handleSaveGitLabCredentials
                      : selectedSource?.id === 'linear' ? handleSaveLinearCredentials
                        : selectedSource?.id === 'harness' ? handleConnectHarness
                          : selectedSource?.id === 'cribl' ? handleSaveCriblCredentials
                            : selectedSource?.id === 'buildkite' ? handleSaveBuildkiteCredentials
                              : selectedSource?.id === 'datadog' ? handleSaveDatadogCredentials
                                : selectedSource?.id === 'pagerduty' ? handleSavePagerDutyCredentials
                                  : handleSaveSlackCredentials
              }
              disabled={loading || (
                selectedSource?.id === 'github' ? (!clientId || !clientSecret)
                  : selectedSource?.id === 'jira' ? (!jiraClientId || !jiraClientSecret)
                    : selectedSource?.id === 'gitlab' ? (!gitlabClientId || !gitlabClientSecret)
                      : selectedSource?.id === 'linear' ? (!linearClientId || !linearClientSecret)
                        : selectedSource?.id === 'harness' ? (!harnessApiKey || !harnessAccountId || !harnessOrgId || !harnessProjectId)
                          : selectedSource?.id === 'cribl' ? (!criblClientId || !criblClientSecret)
                            : selectedSource?.id === 'buildkite' ? (!buildkiteApiToken)
                              : selectedSource?.id === 'datadog' ? (!datadogApiKey || !datadogAppKey)
                                : selectedSource?.id === 'pagerduty' ? (!pagerdutyApiToken)
                                  : (!slackClientId || !slackClientSecret || !slackSigningSecret || !slackRedirectUrl)
              )}
              className={`px-6 py-2.5 ${selectedSource?.id === 'gitlab' ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300' : selectedSource?.id === 'linear' ? 'bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300' : selectedSource?.id === 'harness' ? 'bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300' : selectedSource?.id === 'cribl' ? 'bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300' : selectedSource?.id === 'buildkite' ? 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300' : selectedSource?.id === 'datadog' ? 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300' : selectedSource?.id === 'pagerduty' ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'} disabled:cursor-not-allowed text-white font-medium rounded-lg flex items-center gap-2 transition-colors`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  {selectedSource?.id === 'harness' ? 'Connect' : 'Save & Connect'}
                </>
              )}
            </button>
          </div>
        )}

        {step === STEPS.SELECTION && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setStep(STEPS.SOURCE_PICKER)}
              className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 font-medium rounded-lg transition-colors"
            >
              Back
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-gray-500 hover:text-gray-700 font-medium"
              >
                Skip for now
              </button>
              <button
                onClick={handleConfirmSelection}
                disabled={loading || selectedItems.length === 0}
                className={`px-6 py-2.5 ${provider === 'github' ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                    : provider === 'gitlab' ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300'
                      : provider === 'jira' ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                        : provider === 'linear' ? 'bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300'
                          : provider === 'harness' ? 'bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300'
                            : provider === 'cribl' ? 'bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300'
                              : provider === 'buildkite' ? 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300'
                                : 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300'
                  } disabled:cursor-not-allowed text-white font-medium rounded-lg flex items-center gap-2 transition-colors`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    {provider === 'github' ? 'Sync selected repos'
                      : provider === 'gitlab' ? 'Sync selected projects'
                        : provider === 'jira' ? 'Sync selected projects'
                          : provider === 'linear' ? 'Sync selected teams'
                            : provider === 'harness' ? 'Sync selected pipelines'
                              : provider === 'cribl' ? 'Sync selected groups'
                                : provider === 'buildkite' ? 'Sync selected pipelines'
                                  : 'Sync selected channels'}
                    <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {selectedItems.length}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {(step === STEPS.SUCCESS || step === STEPS.INDEXING) && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            {step === STEPS.SUCCESS && (
              <>
                <button
                  onClick={() => setStep(STEPS.SOURCE_PICKER)}
                  className="px-4 py-2.5 text-gray-600 hover:bg-gray-200 font-medium rounded-lg transition-colors"
                >
                  Connect another tool
                </button>
                <button
                  onClick={() => {
                    onSuccess?.();
                    onClose();
                  }}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  Open Data Sync
                </button>
              </>
            )}
            {step === STEPS.INDEXING && (
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Close and continue later
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
